// Component: StrategyDeepDive.jsx
// Purpose: Strategy deep-dive view with Monitoring, Live Analysis, and Orders tabs

import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import MonitoringTab from './components/MonitoringTab';
import { SessionCoverageChip, SessionCoverageDetail } from './components/SessionCoverageChip';
import LiveAnalysisTab from './components/LiveAnalysisTab';
import { liveInstrumentIdOf, useTradingData } from '../../contexts/TradingDataContext';
import { useAuth } from '../../contexts/AuthContext';
import TradeHistory from '../../components/trading/TradeHistory';
import LivePriceBadge from '../../components/trading/LivePriceBadge';
import DeploymentSparkline from '../../components/trading/DeploymentSparkline';
import { Tabs, Icon, Modal, Breadcrumb, Sheet } from '../../components/ds';
import { useStrategyOrdersBackfill } from '../../hooks/trading/useStrategyOrdersBackfill';
import LiveDeploymentSettingsModal from './components/LiveDeploymentSettingsModal';
import KillSwitchControl from '../../components/trading/KillSwitchControl';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import StrategyService from '../../api/services/strategies';
import OrdersService from '../../api/services/orders';
import { deriveSessionCoverage } from './utils/liveness';
import { countCoverageRefusals, deriveOmsOutcomes } from './utils/omsOutcomes';
import { isExchangeOpenNow } from '../../utils/exchangeHours';

const TABS = [
  { key: 'monitoring', label: 'Monitoring' },
  { key: 'analysis', label: 'Live Analysis' },
  { key: 'orders', label: 'Orders' },
];

const StrategyDeepDive = () => {
  const { deploymentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  //! R4.3 — a row click from the Algo tab's instrument-scoped list (SmartTradeSettings)
  //! navigates here with location.state.initialTab; AlgoDashboard's own navigation carries
  //! no state and keeps the default 'monitoring' (D42).
  const [activeTab, setActiveTab] = useState(() => location.state?.initialTab || 'monitoring');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [stopConfirm, setStopConfirm] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [stopError, setStopError] = useState(null);
  //--> phone-only overflow Sheet (WP3): hosts the chrome items that do not fit the 390px bar
  const [actionsOpen, setActionsOpen] = useState(false);
  const [coverageExpanded, setCoverageExpanded] = useState(false);
  const [rejectedSignalsRows, setRejectedSignalsRows] = useState([]);
  const [rejectedSignalsUnavailable, setRejectedSignalsUnavailable] = useState(false);
  const [coverageNow, setCoverageNow] = useState(() => Date.now());

  const { isCompact, isPhone } = useBreakpoint();
  const { user } = useAuth();
  const { deployments } = useTradingData();

  //--> Find the deployment from live data by deployment_id
  const deployment = useMemo(() => {
    return deployments.find((d) => d.deployment_id === deploymentId) || null;
  }, [deployments, deploymentId]);

  //--> Build composite strategy instance ID for OMS filtering
  const strategyInstanceId = useMemo(() => {
    if (!deployment || !user?.id) return null;
    return `${user.id}:${deployment.strategy_id}:${deployment.version_id}:${liveInstrumentIdOf(deployment)}:${deployment.timeframe}`;
  }, [deployment, user?.id]);

  //--> Fetch strategy-scoped orders, positions, and performance
  const {
    orders: strategyOrders,
    positions: strategyPositions,
    performance: strategyPerformance,
  } = useTradingData(strategyInstanceId ? { strategyInstanceId } : {});

  //--> order-window-starvation fix (issue: trades-view-strategy-backfill) —
  //*   `strategyOrders` above only client-filters the shared global latest-100
  //*   orders fetch (TradingDataContext.jsx:838/:922-933); under real order
  //*   volume this strategy's own orders can age out of that window entirely,
  //*   leaving strategyOrders == [] and the Monitoring/Orders tabs stuck on
  //*   "No trades yet" despite an OPEN position. useStrategyOrdersBackfill
  //*   pulls this strategy's FULL order history via the same gateway-routed
  //*   OrdersService.getOrders({strategyInstanceId}) call LiveAnalysisTab
  //*   already uses for its own (bar-clock-windowed) backfill.
  const { orders: backfillOrders } = useStrategyOrdersBackfill(strategyInstanceId);

  //--> Merge: REST backfill (full history, survives the global-100 window)
  //*   + the live WS-updated scoped orders from context (status/fill updates
  //*   win on overlap — same map-then-overwrite pattern as LiveAnalysisTab's
  //*   historicalOrders+liveOrders merge, LiveAnalysisTab.jsx:207-215).
  const monitoringOrders = useMemo(() => {
    const map = new Map();
    backfillOrders.forEach((o) => map.set(o.id || o.order_id, o));
    (strategyOrders || []).forEach((o) => map.set(o.id || o.order_id, o));
    return Array.from(map.values());
  }, [backfillOrders, strategyOrders]);

  //--> One strategy-scoped rejected-signal fetch feeds coverage in every tab and Monitoring's
  //    signal panels. Failure stays explicit; orders-driven outcomes continue to work.
  useEffect(() => {
    let active = true;
    setRejectedSignalsRows([]);
    setRejectedSignalsUnavailable(false);
    if (!strategyInstanceId) return () => { active = false; };

    const fetchRejected = async () => {
      try {
        const res = await OrdersService.getRejectedSignals(strategyInstanceId);
        const payload = res?.data?.data || res?.data || {};
        if (!active) return;
        if (payload.oms_degraded) {
          console.log('[DIAG] rejected-signals fetch: oms_degraded=true, marking unavailable', { strategyInstanceId });
          setRejectedSignalsRows([]);
          setRejectedSignalsUnavailable(true);
        } else {
          setRejectedSignalsRows(payload.rejected_signals || []);
          setRejectedSignalsUnavailable(false);
        }
      } catch (err) {
        console.log('[DIAG] rejected-signals fetch failed, marking unavailable', { strategyInstanceId, err: err?.message });
        if (active) {
          setRejectedSignalsRows([]);
          setRejectedSignalsUnavailable(true);
        }
      }
    };
    fetchRejected();
    return () => { active = false; };
  }, [strategyInstanceId, deploymentId]);

  const omsOutcomes = useMemo(
    () => deriveOmsOutcomes(monitoringOrders, rejectedSignalsRows),
    [monitoringOrders, rejectedSignalsRows],
  );

  useEffect(() => {
    const intervalId = setInterval(() => setCoverageNow(Date.now()), 30_000);
    return () => clearInterval(intervalId);
  }, []);

  const sessionCoverage = useMemo(
    () => deriveSessionCoverage(
      {
        status: deployment?.status,
        runtimeHealth: deployment?.runtime_health ?? null,
        runtimeOffline: deployment?.runtime_offline === true,
        exchangeOpen: isExchangeOpenNow(deployment?.instrument_exchange),
        timeframe: deployment?.timeframe ?? deployment?.config?.timeframe ?? null,
        session_coverage_evidence: deployment?.session_coverage_evidence ?? null,
      },
      {
        now: coverageNow,
        refusalCounts: countCoverageRefusals(
          omsOutcomes, deployment?.session_coverage_evidence?.session_date,
        ),
      },
    ),
    [coverageNow, deployment, omsOutcomes],
  );

  useEffect(() => {
    setCoverageExpanded(false);
  }, [deploymentId]);

  //--> Stop the running deployment (confirmed). The backend rejects the stop if an open position
  //    exists; surface that message verbatim. On success return to the dashboard (the deployments
  //    WS pushes running→stopped, so the list updates itself).
  const handleStopConfirmed = async () => {
    if (!deployment?.strategy_id || !deployment?.deployment_id) return;
    setStopping(true);
    setStopError(null);
    try {
      await StrategyService.stopDeployment(deployment.strategy_id, deployment.deployment_id);
      setStopConfirm(false);
      navigate('/AlgoDashboard');
    } catch (err) {
      setStopError(err?.response?.data?.detail || err?.message || 'Stop failed — try again.');
    } finally {
      setStopping(false);
    }
  };

  //--> ONE definition per chrome action: the inline buttons (tablet/desktop) and the phone overflow
  //    Sheet both call these. Sheet-originated calls close the Sheet first (see `fromSheet`).
  const openSettings = () => setSettingsOpen(true);
  const restartStrategy = () => { if (deployment?.strategy_id) navigate(`/strategies/edit/${deployment.strategy_id}`); };
  const requestStop = () => { setStopError(null); setStopConfirm(true); };
  const CHROME_ACTIONS = [
    { key: 'settings', label: 'Settings', title: 'Live settings', icon: 'settings', cls: 'btn-secondary', run: openSettings },
    { key: 'restart', label: 'Restart', title: 'Redeploy from the strategy builder', icon: 'refresh', cls: 'btn-secondary', run: restartStrategy },
    { key: 'stop', label: 'Stop', title: 'Stop deployment', icon: 'stop', cls: 'btn-danger', run: requestStop },
  ];
  const runFromSheet = (run) => () => { setActionsOpen(false); run(); };


  //--> Loading spinner while deployment data is not yet available
  if (!deployment) {
    return (
      <div className="flex flex-1 min-h-0 items-center justify-center h-[calc(100vh-6rem)] md:h-auto">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--divider)] border-t-indigo-500" />
      </div>
    );
  }

  const isLive = deployment.mode === 'live';
  const isRunning = deployment.status === 'running';
  //! 144-9: worker absent -> static dot (a pulse beside "No worker" contradicts itself)
  const workerAbsent = sessionCoverage?.status === 'absent';
  const phoneCoverageAttention = sessionCoverage?.status === 'degraded' || sessionCoverage?.status === 'absent';

  return (
    <div className="flex flex-col overflow-hidden bg-main-bg dark:bg-main-dark-bg h-[calc(100vh-6rem)] md:h-auto md:flex-1 md:min-h-0">
      {/* --> Chrome bar (40px, SmartTrade slim-bar classes): crumb + exchange + status + price | tabs | actions */}
      <div data-testid="strategy-chrome-bar" className="h-10 shrink-0 border-b border-[var(--divider)] bg-white dark:bg-bg-2 flex items-center gap-4 px-4">
        <div className={`flex flex-1 items-center gap-2 min-w-0 overflow-hidden${isPhone ? ' truncate' : ''}`}>
          <Breadcrumb
            className="flex-nowrap"
            items={[
              { label: 'Algo Trading', to: '/AlgoDashboard' },
              //* version lives in the tooltip (Gate-1 brief): the crumb shows the name only
              { label: <span title={`Version ${deployment.version_number}`}>{deployment.strategy_name}</span> },
            ]}
          />
          {!isPhone && deployment.instrument_exchange && (
            <span data-testid="exchange-badge" className="text-[10px] px-1.5 py-0.5 rounded bg-bg-3 text-fg-3 font-bold shrink-0">
              {deployment.instrument_exchange}
            </span>
          )}

          {/* Status badge */}
          <span data-testid="status-badge" className={`ds-badge sm shrink-0 ${isLive ? 'profit' : 'info'}`}>
            {isRunning && (
              <span className="relative flex h-2 w-2">
                {!workerAbsent && (
                  <span
                    aria-hidden="true"
                    className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${
                      isLive ? 'bg-[var(--badge-profit-dot)]' : 'bg-[var(--badge-info-dot)]'
                    }`}
                  />
                )}
                <span
                  aria-hidden="true"
                  className={`relative inline-flex h-2 w-2 rounded-full ${
                    isLive ? 'bg-[var(--badge-profit-dot)]' : 'bg-[var(--badge-info-dot)]'
                  }`}
                />
              </span>
            )}
            {isLive ? 'Live' : 'Paper'} &middot; {deployment.status.charAt(0).toUpperCase() + deployment.status.slice(1)}
          </span>

          {(!isPhone || phoneCoverageAttention) && (
            <SessionCoverageChip
              coverage={sessionCoverage}
              compact={isPhone}
              expanded={coverageExpanded}
              onToggle={isPhone
                ? () => setActionsOpen(true)
                : () => setCoverageExpanded((open) => !open)}
            />
          )}

          {/* Live price badge — always visible across all tabs */}
          {!isPhone && <LivePriceBadge deploymentId={deployment?.deployment_id} compact={false} showSymbol />}
          {/* Last-30-min sparkline beside the ONE price pill */}
          {!isPhone && <DeploymentSparkline deployment={deployment} />}
        </div>

        {/* Inline tabs at >=1200px; the bar's border-b is the rule, so the tabs' own hairline is suppressed */}
        {!isCompact && (
          <Tabs
            className="self-stretch shrink-0 !shadow-none"
            value={activeTab}
            onChange={setActiveTab}
            tabs={TABS.map((tab) => ({ label: tab.label, value: tab.key }))}
          />
        )}

        <div data-testid="strategy-chrome-actions" className="flex items-center gap-2 shrink-0">
          {/* #133 — user-scope kill-switch status + Resume */}
          <KillSwitchControl />
          {isPhone ? (
            <button
              type="button"
              className="btn btn-secondary btn-sm btn-icon"
              aria-label="More actions"
              aria-haspopup="dialog"
              onClick={() => setActionsOpen(true)}
            >
              <Icon name="more" size={16} />
            </button>
          ) : (
            CHROME_ACTIONS.map((a) => (
              <button
                key={a.key}
                type="button"
                className={`btn ${a.cls} btn-sm btn-icon`}
                aria-label={a.label}
                title={a.title}
                onClick={a.run}
              >
                <Icon name={a.icon} size={16} />
              </button>
            ))
          )}
        </div>
      </div>

      {!isPhone && coverageExpanded && (
        <SessionCoverageDetail coverage={sessionCoverage} onClose={() => setCoverageExpanded(false)} />
      )}

      {/* --> Compact tab row (<1200px), directly under the bar */}
      {isCompact && (
        <Tabs
          className="shrink-0 min-h-[36px] px-4 bg-white dark:bg-bg-2"
          value={activeTab}
          onChange={setActiveTab}
          tabs={TABS.map((tab) => ({ label: tab.label, value: tab.key }))}
        />
      )}

      {/* --> Tab content. Analysis tab needs a bounded flex-column parent (no scroll)
             so StrategyAnalysisView's percentage-height expand/collapse animation
             has a fixed height to compute against. Other tabs keep the scrolling
             container so long lists work. */}
      {activeTab === 'analysis' ? (
        <div className={`flex-1 min-h-0 ${isCompact ? 'overflow-y-auto' : 'overflow-hidden'} flex flex-col p-2`}>
          <LiveAnalysisTab
            deployment={deployment}
            strategyInstanceId={strategyInstanceId}
          />
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto p-5">
          {activeTab === 'monitoring' && (
            <MonitoringTab
              deployment={deployment}
              strategyInstanceId={strategyInstanceId}
              strategyId={deployment?.strategy_id}
              orders={monitoringOrders}
              positions={strategyPositions}
              performance={strategyPerformance}
              omsOutcomes={omsOutcomes}
              rejectedSignalsUnavailable={rejectedSignalsUnavailable}
            />
          )}
          {activeTab === 'orders' && (
            <TradeHistory
              orders={monitoringOrders}
              positions={strategyPositions}
              mode="compact"
              maxTrades={10}
            />
          )}
        </div>
      )}

      {/* --> Phone overflow Sheet: the same price/sparkline/exchange components and the same action handlers */}
      {isPhone && (
        <Sheet open={actionsOpen} onClose={() => setActionsOpen(false)} title="Deployment actions">
          <div data-testid="strategy-chrome-sheet" className="flex flex-col gap-3 p-4">
            {phoneCoverageAttention && (
              <SessionCoverageDetail coverage={sessionCoverage} layout="sheet" />
            )}
            <div className="flex flex-wrap items-center gap-2">
              {deployment.instrument_exchange && (
                <span data-testid="exchange-badge" className="text-[10px] px-1.5 py-0.5 rounded bg-bg-3 text-fg-3 font-bold shrink-0">
                  {deployment.instrument_exchange}
                </span>
              )}
              <LivePriceBadge deploymentId={deployment?.deployment_id} compact={false} showSymbol />
              <DeploymentSparkline deployment={deployment} />
            </div>
            <div className="flex flex-col gap-2">
              {CHROME_ACTIONS.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  className={`btn ${a.cls} btn-sm w-full justify-start gap-2 min-h-[44px]`}
                  title={a.title}
                  onClick={runFromSheet(a.run)}
                >
                  <Icon name={a.icon} size={16} />
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        </Sheet>
      )}

      {/* --> Live Settings — edit the RUNNING deployment's settings in place (wires the previously
             dead "Settings" button). Confirmation shows before→after + open-trade impact. */}
      <LiveDeploymentSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        deployment={deployment}
        positions={strategyPositions}
      />

      {/* --> Stop confirmation (destructive) — wires the previously dead "Stop" button. */}
      <Modal
        open={stopConfirm}
        onClose={() => !stopping && setStopConfirm(false)}
        size="sm"
        title="Stop deployment?"
        footer={(
          <div className="flex items-center justify-end gap-2 w-full">
            {stopError && <span className="text-xs text-loss mr-auto">{stopError}</span>}
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setStopConfirm(false)} disabled={stopping}>Keep running</button>
            <button type="button" className="btn btn-danger btn-sm" onClick={handleStopConfirmed} disabled={stopping}>
              {stopping ? 'Stopping…' : 'Confirm stop'}
            </button>
          </div>
        )}
      >
        <p className="text-sm text-fg-2">
          This stops <b>{deployment?.strategy_name || deployment?.name || 'this strategy'}</b> — it will place no
          further orders. An open position must be closed before it can stop.
        </p>
      </Modal>
    </div>
  );
};

export default StrategyDeepDive;
